/**
 * ============================================
 * APP CONFIGURATION
 * ============================================
 * 
 * Simplified config for v5.0 - all data comes from backend.
 */

// ============================================
// APP METADATA
// ============================================

export const appMetadata = {
  name: 'Lokko Overseer',
  shortName: 'Lokko',
  description: 'Server monitoring dashboard',
  version: '8.5.9',
  
  branding: {
    logo: '/icons/lokko.png',
    themeColor: '#1e40af',
    backgroundColor: '#0f172a',
  },
};

// ============================================
// POLLING CONFIGURATION
// ============================================
// Since we fetch from backend (not AWS directly), we can poll more frequently

export const pollingConfig = {
  // Main data refresh interval (fetches all environment data)
  dataRefresh: 30000,        // 30 seconds
  
  // Alerts check interval
  alertsRefresh: 10000,      // 10 seconds
  
  // Certificates (slow changing)
  certificatesRefresh: 300000, // 5 minutes
  
  // Prometheus metrics
  metricsRefresh: 60000,     // 1 minute
  
  // Auto-refresh enabled
  autoRefresh: true,
};

// ============================================
// COGNITO CONFIGURATION
// ============================================

const baseRedirectUrl = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000';

export const cognitoConfig = {
  userPoolId: 'us-east-1_l7MJKKFKr',
  userPoolClientId: '3tscada93jfaj5leetlg8tpm5j',
  oauth: {
    domain: 'lokko-overseer.auth.us-east-1.amazoncognito.com',
    redirectSignIn: baseRedirectUrl + '/',
    redirectSignOut: baseRedirectUrl + '/',
    scopes: ['openid', 'email', 'profile'],
    responseType: 'code' as const,
  },
};

export function getCognitoLoginUrl(): string {
  const params = new URLSearchParams({
    client_id: cognitoConfig.userPoolClientId,
    response_type: cognitoConfig.oauth.responseType,
    scope: cognitoConfig.oauth.scopes.join(' '),
    redirect_uri: cognitoConfig.oauth.redirectSignIn,
  });
  return `https://${cognitoConfig.oauth.domain}/login?${params.toString()}`;
}

export function getCognitoLogoutUrl(): string {
  const params = new URLSearchParams({
    client_id: cognitoConfig.userPoolClientId,
    logout_uri: cognitoConfig.oauth.redirectSignOut,
  });
  return `https://${cognitoConfig.oauth.domain}/logout?${params.toString()}`;
}

// ============================================
// ENVIRONMENT CONFIG TYPES
// ============================================

export interface EnvironmentConfig {
  id: string;
  name: string;
  shortName: string;
  color: string;
  isActive: boolean;
}

// ============================================
// SERVICE DEFINITIONS
// ============================================

export type ServiceId = 'nakama' | 'cockroachdb' | 'ec2' | 'asg' | 'alb' | 'redis' | 's3' | 'lambda';
export type ServiceHealthStatus = 'healthy' | 'warning' | 'critical' | 'unknown' | 'disabled';

export interface ServiceDefinition {
  id: ServiceId;
  name: string;
  description: string;
  icon: string;
}

export const services: Record<ServiceId, ServiceDefinition> = {
  nakama: {
    id: 'nakama',
    name: 'Nakama',
    description: 'Game server',
    icon: '🎮',
  },
  cockroachdb: {
    id: 'cockroachdb',
    name: 'CockroachDB',
    description: 'Database cluster',
    icon: '🪳',
  },
  ec2: {
    id: 'ec2',
    name: 'EC2',
    description: 'Compute instances',
    icon: '💻',
  },
  asg: {
    id: 'asg',
    name: 'ASG',
    description: 'Auto Scaling Group',
    icon: '📈',
  },
  alb: {
    id: 'alb',
    name: 'ALB',
    description: 'Load Balancer',
    icon: '⚖️',
  },
  redis: {
    id: 'redis',
    name: 'Redis',
    description: 'Cache server',
    icon: '🔴',
  },
  s3: {
    id: 's3',
    name: 'S3',
    description: 'Object storage',
    icon: '📦',
  },
  lambda: {
    id: 'lambda',
    name: 'Lambda',
    description: 'Serverless functions',
    icon: 'λ',
  },
};

export const statusColors: Record<ServiceHealthStatus, string> = {
  healthy: '#22c55e',
  warning: '#f59e0b',
  critical: '#ef4444',
  unknown: '#6b7280',
  disabled: '#9ca3af',
};

// ============================================
// PLATFORM DEFINITIONS (for player stats)
// ============================================
// Based on C# SaveDeviceType enum:
//   0 = PC
//   1 = Mac
//   2 = Linux
//   3 = iOS
//   4 = Android
//   5 = PlayStation
//   6 = Xbox
//   7 = Switch
//   8 = Unknown

export interface PlatformInfo {
  id: number;
  name: string;
  shortName: string;
  color: string;
}

export const platforms: Record<number, PlatformInfo> = {
  0: { id: 0, name: 'PC', shortName: 'PC', color: '#ffffff' },
  1: { id: 1, name: 'Mac', shortName: 'Mac', color: '#007AFF' },
  2: { id: 2, name: 'Linux', shortName: 'Linux', color: '#f97316' },
  3: { id: 3, name: 'iOS', shortName: 'iOS', color: '#a855f7' },
  4: { id: 4, name: 'Android', shortName: 'Android', color: '#3DDC84' },
  5: { id: 5, name: 'PlayStation', shortName: 'PS', color: '#003791' },
  6: { id: 6, name: 'Xbox', shortName: 'Xbox', color: '#107C10' },
  7: { id: 7, name: 'Nintendo Switch', shortName: 'Switch', color: '#E60012' },
  8: { id: 8, name: 'Unknown', shortName: '?', color: '#6B7280' },
};

export function getPlatformInfo(id: number | string): PlatformInfo {
  const numId = typeof id === 'string' ? parseInt(id, 10) : id;
  return platforms[numId] || { id: numId, name: `Platform ${numId}`, shortName: `P${numId}`, color: '#6B7280' };
}

export function getAllPlatforms(): PlatformInfo[] {
  return Object.values(platforms);
}

export function getPlatformComboLabel(platformIds: number[]): string {
  if (platformIds.length === 0) return 'Unknown';
  if (platformIds.length === 1) return getPlatformInfo(platformIds[0]).name;
  const platformNames = platformIds.sort((a, b) => a - b).map(id => getPlatformInfo(id).shortName).join(' + ');
  return `Cross-Platform: ${platformNames}`;
}

// ============================================
// ALERT CONFIGURATION
// ============================================

export type AlertSeverity = 'critical' | 'warning' | 'info';

// CCU thresholds that trigger milestone alarms (stored locally per device)
export const ccuThresholds = [100, 250, 500, 1500, 2500, 4000, 5000, 7500, 9000, 10000];

// ============================================
// ALERT INFORMATION
// ============================================

export const ALERT_INFO: Record<string, { title: string; description: string; icon: string }> = {
  nakama_down: {
    title: 'Nakama Down',
    description: 'Game server is not responding',
    icon: '🎮',
  },
  cockroachdb_down: {
    title: 'CockroachDB Down',
    description: 'Database is not responding',
    icon: '🪳',
  },
  ec2_down: {
    title: 'EC2 Down',
    description: 'EC2 instances are down',
    icon: '💻',
  },
  asg_down: {
    title: 'ASG Down',
    description: 'Auto Scaling Group has no instances',
    icon: '📈',
  },
  alb_down: {
    title: 'ALB Down',
    description: 'Load balancer is not healthy',
    icon: '⚖️',
  },
  redis_down: {
    title: 'Redis Down',
    description: 'Redis server is down',
    icon: '🔴',
  },
  ccu_threshold: {
    title: 'CCU Milestone',
    description: 'Concurrent users reached a milestone',
    icon: '🎉',
  },
  // Engine fired: ALL envs in priority chain are down (the only audible
  // failover alarm fired when there's no fallback at all).
  failover_down: {
    title: '🆘 ALL SERVERS DOWN — Failover exhausted',
    description: 'Every env in the priority chain is critical. Manual intervention required.',
    icon: '🆘',
  },
  // Engine fired: an env failed and we DID switch over to a healthy
  // fallback. Audible alarm so on-call knows about the failure even though
  // the system self-recovered. Auto-clears at the start of the next
  // switchover (so on switchback the alarm goes away).
  failover_failure_down: {
    title: '🚨 SERVER FAILURE — failover triggered',
    description: 'An active env failed health checks. Engine has switched over to a healthy fallback. Investigate the failed env.',
    icon: '🚨',
  },
  // Info-severity lifecycle events (silent, no alarm) -- pushed by the
  // failover engine for each switchover stage so device notifications fire
  // on Web/Android/iOS via the standard /api/v1/alerts polling pipeline.
  failover_failed_down: {
    title: '🔻 Failover: env DOWN',
    description: 'A monitored env failed health checks. Engine is preparing to switch.',
    icon: '🔻',
  },
  failover_started_down: {
    title: '🔄 Failover: switchover STARTED',
    description: 'Engine has initiated a switchover to a healthy env.',
    icon: '🔄',
  },
  failover_in_progress_down: {
    title: '⏳ Failover: switchover IN PROGRESS',
    description: 'Engine is rewriting remote-config to point at the new env.',
    icon: '⏳',
  },
  failover_done_down: {
    title: '✅ Failover: switchover DONE',
    description: 'Traffic now routes to the new active env. Auto-recovery will return to the preferred env when it recovers.',
    icon: '✅',
  },
  // Admin actions (notify other admin sessions when one admin acts)
  failover_pause_down: {
    title: '⏸️ Failover: PAUSED by admin',
    description: 'Auto-failover is paused. Engine will alert but will NOT switch automatically until resumed.',
    icon: '⏸️',
  },
  failover_resume_down: {
    title: '▶️ Failover: RESUMED by admin',
    description: 'Auto-failover decisions are active again.',
    icon: '▶️',
  },
  failover_force_down: {
    title: '📌 Failover: PINNED by admin',
    description: 'Active endpoint manually pinned. Auto-decisions suspended until released.',
    icon: '📌',
  },
  failover_unforce_down: {
    title: '🔓 Failover: PIN RELEASED by admin',
    description: 'Manual pin released. Auto-failover resumes.',
    icon: '🔓',
  },
  test_alert: {
    title: 'Test Alert',
    description: 'This is a test alert',
    icon: '🧪',
  },
};

// ============================================
// BACKEND URL
// ============================================

// In production, use same origin (empty string). In dev, use localhost:5000
export const BACKEND_URL = import.meta.env.VITE_BACKEND_URL ?? '';
